import { useState, useEffect, useContext, useRef } from 'react';

import axiosApiInstance from '@/api/shared/axios-instance';
import { Context } from '@/context';
import { REDUCERS } from '@/constants/reducers';
import URL_FACTORY from '@/helpers/url-helper';
import { currentSeason } from '@/constants/site';

// Constantes para caché
const STORAGE_KEY = 'analitica_fantasy_jugadores_plantilla';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos en milisegundos

// Funciones de caché en localStorage (solo en cliente)
const saveToCache = (jugadores, jornadas) => {
    if (typeof window === 'undefined') return;

    try {
        const dataToStore = {
            jugadores,
            jornadas,
            timestamp: Date.now(),
            expiresAt: Date.now() + CACHE_DURATION,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
        console.error('Error al guardar caché de jugadores:', error);
    }
};

const loadFromCache = () => {
    if (typeof window === 'undefined') return null;

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const data = JSON.parse(stored);
        const now = Date.now();

        // Verificar si ha expirado
        if (now > data.expiresAt) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        return {
            jugadores: data.jugadores,
            jornadas: data.jornadas,
        };
    } catch (error) {
        console.error('Error al cargar caché de jugadores:', error);
        return null;
    }
};

// Mapper: convierte propiedades minificadas a nombres largos
const mapEstadisticasFantasy = (stats) => {
    if (!stats) return null;
    return {
        points: stats.pts,
        racha: stats.racha,
        played: stats.played,
    };
};

const mapModosPuntuacion = (modo) => {
    if (!modo) return null;
    return {
        mode: modo.mode,
        home: mapEstadisticasFantasy(modo.home),
        away: mapEstadisticasFantasy(modo.away),
        total: mapEstadisticasFantasy(modo.total),
    };
};

const mapFantasyData = (fantasy) => {
    if (!fantasy) return null;
    return {
        positionId: fantasy.posId,
        marketValue: fantasy.mv,
        subida: fantasy.sub,
        modos: fantasy.modes?.map(mapModosPuntuacion) || null,
    };
};

const mapEstadisticasGenerales = (stats) => {
    if (!stats) return null;
    return {
        goles: stats.g,
        asistencias: stats.a,
        amarillas: stats.y,
        rojas: stats.r,
        titularidades: stats.t,
        suplencias: stats.s,
        minutos: stats.m,
        changeIn: stats.ci,
        changeOut: stats.co,
    };
};

const mapTeamBasic = (team) => {
    if (!team) return null;
    return {
        id: team.id,
        name: team.name,
        goals: team.goals,
    };
};

const mapFixtureJornada = (fixture) => {
    if (!fixture) return null;
    return {
        fixtureId: fixture.fId,
        date: fixture.date,
        status: fixture.status,
        homeTeam: mapTeamBasic(fixture.hTeam),
        awayTeam: mapTeamBasic(fixture.aTeam),
    };
};

const mapJornada = (jornada) => {
    if (!jornada) return null;
    return {
        round: jornada.round,
        fixtures: jornada.fixtures?.map(mapFixtureJornada) || [],
    };
};

export const mapJugadorPlantilla = (jugador) => {
    return {
        name: jugador.n,
        slug: jugador.sl,
        birthDate: jugador.bd,
        nationality: jugador.nat,
        birthPlace: jugador.bp,
        playerId: jugador.pId,
        teamId: jugador.tId,
        positionId: jugador.posId,
        estadisticas: mapEstadisticasGenerales(jugador.stats),
        laLigaFantasy: mapFantasyData(jugador.llf),
        fantasyMarca: mapFantasyData(jugador.fm),
        misterFantasy: mapFantasyData(jugador.mf),
        biwenger: mapFantasyData(jugador.biw),
        chance: jugador.chance,
        updatedAt: jugador.updatedAt,
        status: jugador.status,
        statusLevel: jugador.statusLevel,
        comentario: jugador.com,
        isBufon: jugador.buf,
        isCapitan: jugador.cap,
        isAriete: jugador.ari,
        isRevulsivo: jugador.rev,
        isChollo: jugador.cho,
        tier: jugador.tier,
    };
};

// Promise singleton para evitar múltiples requests
let fetchPromise = null;

export const useJugadoresPlantilla = (season = currentSeason) => {
    const { state, dispatch } = useContext(Context);
    const [loading, setLoading] = useState(false);

    // Ref para controlar si este hook ya ejecutó el fetch
    const hasFetchedRef = useRef(false);

    const obtenerJugadores = async (skipCache = false) => {
        // Si ya existe una promesa en progreso, esperarla y retornar
        if (fetchPromise) {
            await fetchPromise;
            return;
        }

        // Intentar cargar del caché primero (solo en cliente y si no se solicita skip)
        if (!skipCache && typeof window !== 'undefined') {
            const cachedData = loadFromCache();
            if (cachedData) {
                // Actualizar el state global con datos del caché
                dispatch({
                    type: REDUCERS.UPDATE_JUGADORES,
                    payload: {
                        jugadores: cachedData.jugadores,
                        jornadas: cachedData.jornadas,
                    },
                });
                return;
            }
        }

        // Crear la promesa y almacenarla
        fetchPromise = (async () => {
            try {
                setLoading(true);

                const url = URL_FACTORY.GetJugadoresPlantillas(season);
                const response = await axiosApiInstance.get(url);

                if (response.data?.jugadores) {
                    // Mapear los datos de propiedades minificadas a nombres largos
                    const jugadoresMapeados = response.data.jugadores.map(mapJugadorPlantilla);
                    const jornadasMapeadas = response.data.jornadas?.map(mapJornada) || [];

                    // Guardar en caché
                    saveToCache(jugadoresMapeados, jornadasMapeadas);

                    // Actualizar el state global con los jugadores mapeados
                    dispatch({
                        type: REDUCERS.UPDATE_JUGADORES,
                        payload: {
                            jugadores: jugadoresMapeados,
                            jornadas: jornadasMapeadas,
                        },
                    });
                }
            } catch (err) {
                console.error('Error al obtener jugadores:', err);
                throw err;
            } finally {
                setLoading(false);
                setTimeout(() => {
                    fetchPromise = null;
                }, 100);
            }
        })();

        await fetchPromise;
    };

    useEffect(() => {
        // Si este hook ya hizo el fetch, no hacerlo de nuevo
        if (hasFetchedRef.current) {
            return;
        }

        // Si ya hay jugadores en el state, no hacer fetch
        if (state.jugadores && state.jugadores.length > 0) {
            hasFetchedRef.current = true;
            return;
        }

        hasFetchedRef.current = true;
        obtenerJugadores();
    }, []);

    return {
        jugadores: state.jugadores,
        jornadas: state.jornadas,
        loading,
        refetch: obtenerJugadores,
    };
};
